// Implements: F-SHR-013, F-UIX-007
/**
 * Mockup v3 P8: which share the "Ver como visitante" overlay shows, if any.
 *
 * `open` remembers the control that had the focus (the button or the row's eye), and `close` gives
 * the focus back to it once the overlay is gone, provided it is still on the page.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import type { VisitorPreviewTarget } from "../../../types/sharing";

export interface UseVisitorPreviewResult {
  readonly open: (target: VisitorPreviewTarget) => void;
  readonly close: () => void;
  readonly target: VisitorPreviewTarget | null;
}

export function useVisitorPreview(): UseVisitorPreviewResult {
  const [target, setTarget] = useState<VisitorPreviewTarget | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const returnFocus = useRef(false);

  useEffect(() => {
    if (target !== null || !returnFocus.current) return;
    returnFocus.current = false;
    if (opener.current?.isConnected === true) opener.current.focus();
    opener.current = null;
  }, [target]);

  const open = useCallback((next: VisitorPreviewTarget): void => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setTarget(next);
  }, []);

  const close = useCallback((): void => {
    returnFocus.current = true;
    setTarget(null);
  }, []);

  return { open, close, target };
}
